/**
 * InviteCoachModal - Modal for searching and inviting users as coach
 * V2: Anyone can be invited as coach (no profile required)
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Search,
  UserPlus,
  Dumbbell,
  Loader2,
  Eye,
  BarChart2,
  Camera,
  Ruler,
  User,
  BadgeCheck,
} from 'lucide-react';
import { useCoachStore, COACH_SPECIALIZATIONS } from '../../stores/coachStore';

const InviteCoachModal = () => {
  const {
    isInviteModalOpen,
    closeInviteModal,
    selectedUserForInvite,
    userSearchResults,
    isSearchingUsers,
    searchUsers,
    clearUserSearchResults,
    inviteCoach,
  } = useCoachStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({
    canViewWorkouts: true,
    canViewAnalytics: true,
    canViewProgressPhotos: false,
    canViewBodyMeasurements: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isInviteModalOpen) {
      setSearchQuery('');
      setSelectedUser(selectedUserForInvite);
      setPermissions({
        canViewWorkouts: true,
        canViewAnalytics: true,
        canViewProgressPhotos: false,
        canViewBodyMeasurements: false,
      });
      clearUserSearchResults();

      // Load initial user list
      if (!selectedUserForInvite) {
        searchUsers('');
      }
    }
  }, [isInviteModalOpen, selectedUserForInvite, searchUsers, clearUserSearchResults]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isInviteModalOpen && searchQuery !== undefined) {
        searchUsers(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, isInviteModalOpen, searchUsers]);

  const handleInvite = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await inviteCoach(selectedUser.id, permissions);
      closeInviteModal();
    } catch (error) {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isInviteModalOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeInviteModal}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-500" />
              Koç Davet Et
            </h2>
            <button
              onClick={closeInviteModal}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
            {/* Search */}
            {!selectedUser && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Kullanıcı ara (e-posta veya isim)..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
                />
              </div>
            )}

            {/* User Selection */}
            {!selectedUser && (
              <div className="space-y-2">
                <p className="text-sm text-gray-400 mb-2">Kullanıcılar:</p>
                {isSearchingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                  </div>
                ) : userSearchResults.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Kullanıcı bulunamadı
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {userSearchResults.map((user) => (
                      <motion.button
                        key={user.id}
                        onClick={() => setSelectedUser(user)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        className="w-full p-3 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-gray-600 rounded-lg text-left transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user.has_coach_profile ? 'bg-orange-500/10' : 'bg-gray-700'
                          }`}>
                            {user.has_coach_profile ? (
                              <Dumbbell className="w-5 h-5 text-orange-400" />
                            ) : (
                              <User className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{user.display_name}</p>
                              {user.has_coach_profile && (
                                <BadgeCheck className="w-4 h-4 text-orange-400 flex-shrink-0" title="Koç Profili Var" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            {user.coach_specializations?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {user.coach_specializations.slice(0, 3).map((spec) => (
                                  <span
                                    key={spec}
                                    className="text-xs text-gray-400"
                                  >
                                    {COACH_SPECIALIZATIONS[spec]?.emoji}{' '}
                                    {COACH_SPECIALIZATIONS[spec]?.label || spec}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected User */}
            {selectedUser && (
              <>
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      selectedUser.has_coach_profile ? 'bg-orange-500/10' : 'bg-gray-700'
                    }`}>
                      {selectedUser.has_coach_profile ? (
                        <Dumbbell className="w-6 h-6 text-orange-400" />
                      ) : (
                        <User className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{selectedUser.display_name}</p>
                        {selectedUser.has_coach_profile && (
                          <BadgeCheck className="w-4 h-4 text-orange-400" title="Koç Profili Var" />
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{selectedUser.email}</p>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="text-sm text-gray-400 hover:text-white"
                    >
                      Değiştir
                    </button>
                  </div>
                  {selectedUser.coach_bio && (
                    <p className="text-sm text-gray-400 mt-3">{selectedUser.coach_bio}</p>
                  )}
                </div>

                {/* Permissions */}
                <div>
                  <h3 className="font-medium mb-3">Erişim İzinleri</h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Koçun hangi verilerinizi görebileceğini belirleyin
                  </p>

                  <div className="space-y-3">
                    <PermissionToggle
                      icon={<Eye className="w-4 h-4" />}
                      label="Antrenman Kayıtları"
                      description="Egzersiz, set, tekrar ve ağırlık bilgileri"
                      checked={permissions.canViewWorkouts}
                      onChange={(checked) =>
                        setPermissions((p) => ({ ...p, canViewWorkouts: checked }))
                      }
                    />

                    <PermissionToggle
                      icon={<BarChart2 className="w-4 h-4" />}
                      label="Analizler ve İstatistikler"
                      description="İlerleme grafikleri ve özet istatistikler"
                      checked={permissions.canViewAnalytics}
                      onChange={(checked) =>
                        setPermissions((p) => ({ ...p, canViewAnalytics: checked }))
                      }
                    />

                    <PermissionToggle
                      icon={<Camera className="w-4 h-4" />}
                      label="İlerleme Fotoğrafları"
                      description="Vücut değişim fotoğrafları"
                      checked={permissions.canViewProgressPhotos}
                      onChange={(checked) =>
                        setPermissions((p) => ({ ...p, canViewProgressPhotos: checked }))
                      }
                      disabled
                      comingSoon
                    />

                    <PermissionToggle
                      icon={<Ruler className="w-4 h-4" />}
                      label="Vücut Ölçümleri"
                      description="Kilo, boy, vücut ölçüleri"
                      checked={permissions.canViewBodyMeasurements}
                      onChange={(checked) =>
                        setPermissions((p) => ({ ...p, canViewBodyMeasurements: checked }))
                      }
                      disabled
                      comingSoon
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-3">
            <button
              onClick={closeInviteModal}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              İptal
            </button>
            <motion.button
              onClick={handleInvite}
              disabled={!selectedUser || isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4" />
              )}
              Davet Gönder
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Permission Toggle Component
const PermissionToggle = ({
  icon,
  label,
  description,
  checked,
  onChange,
  disabled,
  comingSoon,
}) => {
  return (
    <label
      className={`flex items-start gap-3 p-3 bg-gray-800/50 border border-gray-700 rounded-lg cursor-pointer transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
      }`}
    >
      <div className={`mt-0.5 ${checked ? 'text-orange-400' : 'text-gray-500'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{label}</span>
          {comingSoon && (
            <span className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">
              Yakında
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          disabled={disabled}
          className="sr-only"
        />
        <div
          className={`w-10 h-6 rounded-full transition-colors ${
            checked ? 'bg-orange-500' : 'bg-gray-700'
          }`}
        >
          <div
            className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform mt-1 ${
              checked ? 'translate-x-5' : 'translate-x-1'
            }`}
          />
        </div>
      </div>
    </label>
  );
};

export default InviteCoachModal;
